import React, { useMemo, useRef, useState } from 'react';
import { Boxes, Upload, Plus, Search, Pencil, Trash2, X, Download, Star, ListChecks, AlertTriangle } from 'lucide-react';
import { useCatalog } from '../hooks/useCatalog';
import { useBusinessStore } from '../store/useBusinessStore';
import { isModuleEnabled } from '../utils/businessConfigHelpers';
import { TAX_KIND_LABELS } from '../utils/quotes';
import { buildImport, formatPrice, matchesSearch, parseCsv } from '../utils/catalog';
import { readXlsxRows } from '../utils/xlsx';
import ProductsTabs from '../components/ProductsTabs';

const inputCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';
const labelCls = 'text-[10px] uppercase font-bold text-seryi-text';
const PAGE_SIZE = 50;

const downloadTemplate = () => {
  const csv = '﻿Código;Nombre;Categoría;Unidad;Existencias;Precio;Precio Mayorista\nC-001;Cable eléctrico 2x12;Cables;m;120;12.500;11.000\nB-020;Breaker 20A;Protecciones;und;8;18.900;16.000\n';
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = 'plantilla_catalogo.csv'; a.click();
  URL.revokeObjectURL(url);
};

function ImportPanel({ onClose, onImport, importing, withTax = false }) {
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState('');
  // Con el módulo de cotizaciones encendido el IVA se guarda aparte y las cotizaciones lo suman: ahí los precios que el archivo marca con IVA incluido se guardan sin IVA.
  const [pricesIncludeTax, setPricesIncludeTax] = useState(withTax);
  const inputRef = useRef(null);

  const [dragging, setDragging] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      let parsed;
      if (/\.xlsx$/i.test(file.name)) {
        parsed = readXlsxRows(await file.arrayBuffer());
      } else {
        // Excel en español suele guardar el CSV en Windows-1252: si el UTF-8 trae caracteres inválidos se relee así.
        let text = await file.text();
        if (text.includes('\uFFFD')) text = new TextDecoder('windows-1252').decode(await file.arrayBuffer());
        parsed = parseCsv(text);
      }
      setRows(parsed);
      setResult(buildImport(parsed, { pricesIncludeTax }));
    } catch (err) {
      setRows(null);
      setResult({ ok: false, error: err?.message || 'No pude leer el archivo.' });
    }
  };

  const onFile = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer?.files?.[0]); };
  const openPicker = () => inputRef.current?.click();

  const toggleTax = (checked) => {
    setPricesIncludeTax(checked);
    if (rows) setResult(buildImport(rows, { pricesIncludeTax: checked }));
  };

  const omitted = result?.ok ? result.errors.filter((er) => !er.warning).length : 0;

  return (
    <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-3xl space-y-4 shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-sm text-seryi-heading">Importar productos desde Excel o CSV</h3>
          <p className="text-[11px] text-seryi-text mt-1">
            Sube tu lista de productos en <b>Excel (.xlsx)</b> o CSV; sirve el archivo que exporta tu programa de inventario o facturación tal como sale. La primera fila lleva los títulos: <b>Código</b>, <b>Nombre</b>, Categoría, Unidad, Existencias y una columna por cada lista de precios (<b>Precio</b>, Precio Mayorista, Precio Distribuidor…).
            Si el código ya existe, el producto se actualiza; no se duplica.
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar importación" className="text-seryi-text hover:text-seryi-heading"><X size={16} /></button>
      </div>
      {/* Caja para cargar el archivo: se hace clic en cualquier parte o se arrastra el archivo encima. */}
      <div
        role="button" tabIndex={0} aria-label="Elegir archivo de productos"
        onClick={openPicker}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors focus:outline-none focus:border-seryi-green ${dragging ? 'border-seryi-green bg-seryi-green/10' : 'border-seryi-green/40 bg-seryi-bg-dark/60 hover:border-seryi-green hover:bg-seryi-green/5'}`}
      >
        <Upload size={26} className="mx-auto text-seryi-green" />
        <p className="mt-2 text-sm font-bold text-seryi-heading">{fileName ? 'Cambiar archivo' : 'Haz clic aquí para elegir tu archivo'}</p>
        <p className="mt-0.5 text-[11px] text-seryi-text">o arrástralo y suéltalo en esta caja · Excel (.xlsx) o CSV</p>
        {fileName && <p className="mt-2 inline-block rounded-full bg-seryi-green/15 px-3 py-0.5 text-[11px] font-bold text-seryi-green">Archivo elegido: {fileName}</p>}
      </div>
      <input ref={inputRef} type="file" accept=".csv,text/csv,.txt,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} aria-label="Archivo de productos" tabIndex={-1} className="sr-only" />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={downloadTemplate} className="text-[11px] font-bold text-seryi-green flex items-center gap-1 hover:underline"><Download size={12} /> Descargar plantilla</button>
      </div>
      {!result && (
        <p className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
          <b>Paso a paso:</b> elige el archivo y aquí abajo verás un resumen con los productos y las listas de precios. Si tu archivo indica que los precios ya traen el IVA incluido,
          aparecerá además una casilla <b>«Guardar los precios sin IVA»</b>, ya marcada, para que las cotizaciones no cobren el IVA dos veces. Al final pulsa <b>Importar</b>.
        </p>
      )}

      {result && !result.ok && (
        <div className="p-3 rounded-xl border border-red-500/40 bg-red-500/10 text-xs text-red-300 flex gap-2"><AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{result.error}</span></div>
      )}
      {result?.ok && (
        <div className="space-y-3">
          <div className="p-3 rounded-xl border border-seryi-green/40 bg-seryi-green/10 text-xs text-seryi-heading space-y-1">
            <p><b>{result.products.length}</b> producto{result.products.length === 1 ? '' : 's'} listo{result.products.length === 1 ? '' : 's'} de {fileName}.</p>
            <p>Listas de precios: {result.lists.map((l) => l.name).join(', ')}.</p>
            {omitted > 0 && <p className="text-amber-300">{omitted} fila{omitted === 1 ? '' : 's'} se omitirán (abajo).</p>}
          </div>
          {result.includedRows > 0 && (
            <label className="flex items-start gap-2 p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-xs text-amber-100 cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={pricesIncludeTax} onChange={(e) => toggleTax(e.target.checked)} aria-label="Guardar los precios sin IVA" />
              <span>
                Tu archivo marca {result.includedRows} producto{result.includedRows === 1 ? '' : 's'} con el <b>IVA incluido</b> en el precio. <b>Guardar los precios sin IVA</b> para que las cotizaciones no cobren el IVA dos veces
                {result.example && <> (ej. {result.example.name}, lista {result.example.list}: {formatPrice(result.example.listed)} con IVA {result.example.rate} % → {formatPrice(result.example.withoutTax)} + IVA).</>}
                {' '}Desmárcalo si tus listas ya son sin IVA o si no vas a cotizar con IVA.
                {!withTax && <> El módulo de cotizaciones está apagado: el IVA no se guardará aparte.</>}
              </span>
            </label>
          )}
          {result.errors.length > 0 && (
            <ul className="max-h-32 overflow-auto text-[11px] text-amber-200 space-y-0.5 bg-seryi-bg-dark rounded-xl p-3">
              {result.errors.slice(0, 50).map((er, i) => <li key={`${er.line}-${i}`}>Línea {er.line}: {er.message}</li>)}
              {result.errors.length > 50 && <li>…y {result.errors.length - 50} más.</li>}
            </ul>
          )}
          <div className="overflow-auto max-h-48 rounded-xl border border-seryi-bg-light">
            <table className="w-full text-[11px] text-seryi-heading">
              <thead className="bg-seryi-bg-dark text-seryi-text"><tr><th className="p-2 text-left">Código</th><th className="p-2 text-left">Producto</th>{result.hasTax && withTax && <th className="p-2 text-left">Impuesto</th>}{result.lists.map((l) => <th key={l.key} className="p-2 text-right">{l.name}</th>)}</tr></thead>
              <tbody>
                {result.products.slice(0, 8).map((p) => (
                  <tr key={p.sku} className="border-t border-seryi-bg-light"><td className="p-2">{p.sku}</td><td className="p-2">{p.name}</td>{result.hasTax && withTax && <td className="p-2">{p.tax_kind === 'iva' || p.tax_kind === 'consumo' ? `${TAX_KIND_LABELS[p.tax_kind] || p.tax_kind} ${p.tax_rate} %` : (TAX_KIND_LABELS[p.tax_kind] || '—')}</td>}{result.lists.map((l) => <td key={l.key} className="p-2 text-right">{formatPrice(p.prices[l.key])}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" disabled={importing} onClick={async () => { if (await onImport(result, { withTax })) { setResult(null); setRows(null); setFileName(''); if (inputRef.current) inputRef.current.value = ''; onClose(); } }}
            className="bg-seryi-green text-seryi-bg-dark font-extrabold text-xs px-4 py-2 rounded-xl disabled:opacity-50">
            {importing ? 'Importando…' : `Importar ${result.products.length} producto${result.products.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}
    </div>
  );
}

function ProductForm({ product, prices, lists, onSave, onClose, showTax = false }) {
  const [f, setF] = useState(() => ({
    sku: product?.sku || '', name: product?.name || '', category: product?.category || '', unit: product?.unit || '',
    stock: product?.stock ?? '', active: product?.active ?? true, tax_kind: product?.tax_kind || 'iva', tax_rate: product?.tax_rate ?? 0,
    prices: Object.fromEntries(lists.map((l) => [l.key, prices?.[l.key] ?? '']))
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={product ? 'Editar producto' : 'Nuevo producto'}>
      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm text-seryi-heading">{product ? 'Editar producto' : 'Nuevo producto'}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="text-seryi-text hover:text-seryi-heading"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><label htmlFor="p-sku" className={labelCls}>Código</label><input id="p-sku" className={inputCls} value={f.sku} onChange={(e) => set('sku', e.target.value)} /></div>
          <div className="space-y-1"><label htmlFor="p-name" className={labelCls}>Nombre</label><input id="p-name" className={inputCls} value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
          <div className="space-y-1"><label htmlFor="p-cat" className={labelCls}>Categoría</label><input id="p-cat" className={inputCls} value={f.category} onChange={(e) => set('category', e.target.value)} /></div>
          <div className="space-y-1"><label htmlFor="p-unit" className={labelCls}>Unidad</label><input id="p-unit" className={inputCls} value={f.unit} placeholder="und, m, kg…" onChange={(e) => set('unit', e.target.value)} /></div>
          <div className="space-y-1"><label htmlFor="p-stock" className={labelCls}>Existencias</label><input id="p-stock" type="number" min="0" className={inputCls} value={f.stock} onChange={(e) => set('stock', e.target.value)} /></div>
          <label className="flex items-center gap-2 text-xs text-seryi-heading self-end pb-2"><input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Activo (el bot lo ofrece)</label>
          {showTax && (
            <>
              <div className="space-y-1">
                <label htmlFor="p-taxkind" className={labelCls}>Tipo de impuesto</label>
                <select id="p-taxkind" className={inputCls} value={f.tax_kind} onChange={(e) => setF((p) => ({ ...p, tax_kind: e.target.value, ...(['excluido', 'exento'].includes(e.target.value) ? { tax_rate: 0 } : {}) }))}>
                  {Object.entries(TAX_KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label htmlFor="p-taxrate" className={labelCls}>Tarifa del impuesto (%)</label>
                <input id="p-taxrate" type="number" min="0" max="100" step="any" className={inputCls} disabled={['excluido', 'exento'].includes(f.tax_kind)} value={f.tax_rate} onChange={(e) => set('tax_rate', e.target.value)} />
              </div>
            </>
          )}
        </div>
        <div className="space-y-2">
          <p className={labelCls}>Precios por lista</p>
          {lists.length === 0 && <p className="text-[11px] text-amber-300">Primero crea una lista de precios (botón "Listas de precios").</p>}
          <div className="grid grid-cols-2 gap-3">
            {lists.map((l) => (
              <div key={l.key} className="space-y-1">
                <label htmlFor={`pr-${l.key}`} className="text-[10px] text-seryi-text">{l.name}{l.is_default ? ' (predeterminada)' : ''}</label>
                <input id={`pr-${l.key}`} type="number" min="0" step="any" className={inputCls} value={f.prices[l.key]} onChange={(e) => setF((p) => ({ ...p, prices: { ...p.prices, [l.key]: e.target.value } }))} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs font-bold text-seryi-text px-3 py-2">Cancelar</button>
          <button type="button" disabled={saving || lists.length === 0} onClick={async () => { setSaving(true); const { tax_kind, tax_rate, ...rest } = f; const ok = await onSave({ id: product?.id, ...rest, ...(showTax ? { tax_kind, tax_rate } : {}) }); setSaving(false); if (ok) onClose(); }}
            className="bg-seryi-green text-seryi-bg-dark font-extrabold text-xs px-4 py-2 rounded-xl disabled:opacity-50">{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

function ListsPanel({ lists, onAdd, onDefault, onDelete, onClose }) {
  const [name, setName] = useState('');
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light p-5 rounded-3xl space-y-3 shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-sm text-seryi-heading">Listas de precios</h3>
          <p className="text-[11px] text-seryi-text mt-1">El bot usa la lista del cliente; si el cliente no tiene una asignada, usa la predeterminada.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Cerrar listas" className="text-seryi-text hover:text-seryi-heading"><X size={16} /></button>
      </div>
      <ul className="space-y-1">
        {lists.map((l) => (
          <li key={l.key} className="flex items-center justify-between bg-seryi-bg-dark rounded-xl px-3 py-2 text-xs text-seryi-heading">
            <span>{l.name} {l.is_default && <span className="ml-1 text-[10px] text-seryi-green font-bold">Predeterminada</span>}</span>
            <span className="flex items-center gap-2">
              {!l.is_default && <button type="button" onClick={() => onDefault(l.key)} title="Hacer predeterminada" aria-label={`Hacer predeterminada ${l.name}`} className="text-seryi-text hover:text-seryi-green"><Star size={13} /></button>}
              {!l.is_default && <button type="button" onClick={() => onDelete(l.key)} title="Eliminar lista" aria-label={`Eliminar lista ${l.name}`} className="text-seryi-text hover:text-red-400"><Trash2 size={13} /></button>}
            </span>
          </li>
        ))}
        {lists.length === 0 && <li className="text-[11px] text-seryi-text">Aún no hay listas. Se crean solas al importar un archivo, o crea la primera aquí.</li>}
      </ul>
      <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); if (await onAdd(name)) setName(''); }}>
        <input className={inputCls} placeholder="Nueva lista (ej. Mayorista)" aria-label="Nombre de la nueva lista" value={name} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="bg-seryi-green text-seryi-bg-dark font-extrabold text-xs px-3 rounded-xl whitespace-nowrap">Crear</button>
      </form>
    </div>
  );
}

export default function CatalogPage() {
  const { products, lists, priceMap, defaultKey, loading, tableMissing, importing, importProducts, saveProduct, toggleProduct, deleteProduct, addList, setDefaultList, deleteList } = useCatalog();
  const { masterDb, activeBusinessId } = useBusinessStore();
  const showTax = isModuleEnabled((Array.isArray(masterDb) ? masterDb : []).find((b) => String(b.id) === String(activeBusinessId)), 'feature_cotizaciones');
  const [panel, setPanel] = useState(null); // 'import' | 'lists' | null
  const [editing, setEditing] = useState(null); // null | 'new' | producto
  const [query, setQuery] = useState('');
  const [listKey, setListKey] = useState('');
  const [page, setPage] = useState(0);

  const shownList = lists.some((l) => l.key === listKey) ? listKey : defaultKey;
  const filtered = useMemo(() => products.filter((p) => matchesSearch(p, query)), [products, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  if (tableMissing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261003a_catalog.sql</code> en Supabase para usar el catálogo.</div>;
  }

  return (
    <div className="space-y-5 max-w-6xl">
      <ProductsTabs />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><Boxes size={20} /> Catálogo con listas de precios</h2>
          <p className="text-xs text-seryi-text">Los productos y precios que Keyla IA consulta para responder a tus clientes. Nunca da un precio que no esté aquí.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setPanel(panel === 'lists' ? null : 'lists')} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><ListChecks size={13} /> Listas de precios</button>
          <button type="button" onClick={() => setPanel(panel === 'import' ? null : 'import')} className="border border-seryi-bg-light text-seryi-heading text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5"><Upload size={13} /> Importar Excel/CSV</button>
          <button type="button" onClick={() => setEditing('new')} className="bg-seryi-green text-seryi-bg-dark text-xs font-extrabold px-3 py-2 rounded-xl flex items-center gap-1.5"><Plus size={13} /> Nuevo producto</button>
        </div>
      </div>

      {panel === 'import' && <ImportPanel onClose={() => setPanel(null)} onImport={importProducts} importing={importing} withTax={showTax} />}
      {panel === 'lists' && <ListsPanel lists={lists} onAdd={addList} onDefault={setDefaultList} onDelete={deleteList} onClose={() => setPanel(null)} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
          <input className={`${inputCls} pl-8`} placeholder="Buscar por código, nombre o categoría" aria-label="Buscar productos" value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} />
        </div>
        {lists.length > 1 && (
          <select aria-label="Lista de precios a mostrar" className={`${inputCls} w-auto`} value={shownList} onChange={(e) => setListKey(e.target.value)}>
            {lists.map((l) => <option key={l.key} value={l.key}>Precios: {l.name}</option>)}
          </select>
        )}
        <span className="text-[11px] text-seryi-text">{filtered.length} de {products.length} producto{products.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <p className="text-xs text-seryi-text">Cargando…</p>
      ) : products.length === 0 ? (
        <div className="p-8 text-center text-xs text-seryi-text border border-dashed border-seryi-bg-light rounded-3xl">
          Aún no hay productos. Importa tu lista desde Excel o CSV, o agrega el primero con "Nuevo producto".
        </div>
      ) : (
        <div className="overflow-auto rounded-3xl border border-seryi-bg-light">
          <table className="w-full text-xs text-seryi-heading">
            <thead className="bg-seryi-bg-dark text-seryi-text text-[10px] uppercase">
              <tr><th className="p-3 text-left">Código</th><th className="p-3 text-left">Producto</th><th className="p-3 text-left">Categoría</th><th className="p-3 text-right">Existencias</th><th className="p-3 text-right">Precio</th><th className="p-3 text-center">Activo</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const own = priceMap.get(p.id) || {};
                const price = own[shownList] ?? own[defaultKey];
                return (
                  <tr key={p.id} className={`border-t border-seryi-bg-light ${p.active ? '' : 'opacity-50'}`}>
                    <td className="p-3 font-mono">{p.sku || '—'}</td>
                    <td className="p-3 font-semibold">{p.name}</td>
                    <td className="p-3">{p.category || '—'}</td>
                    <td className="p-3 text-right">{p.stock ?? '—'} {p.unit || ''}</td>
                    <td className="p-3 text-right font-bold text-emerald-400">{formatPrice(price)}{own[shownList] === undefined && price !== undefined ? <span className="text-[9px] text-seryi-text ml-1">(lista predeterminada)</span> : null}</td>
                    <td className="p-3 text-center"><input type="checkbox" aria-label={`Activo: ${p.name}`} checked={p.active} onChange={(e) => toggleProduct(p.id, e.target.checked)} /></td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button type="button" aria-label={`Editar ${p.name}`} onClick={() => setEditing(p)} className="text-seryi-text hover:text-seryi-green mr-2"><Pencil size={13} /></button>
                      <button type="button" aria-label={`Eliminar ${p.name}`} onClick={() => { if (window.confirm(`¿Eliminar "${p.name}" y sus precios?`)) deleteProduct(p.id); }} className="text-seryi-text hover:text-red-400"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-seryi-text">Ningún producto coincide con la búsqueda.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs text-seryi-text">
          <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)} className="px-3 py-1 border border-seryi-bg-light rounded-lg disabled:opacity-40">Anterior</button>
          <span>Página {current + 1} de {pages}</span>
          <button type="button" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} className="px-3 py-1 border border-seryi-bg-light rounded-lg disabled:opacity-40">Siguiente</button>
        </div>
      )}

      {editing && (
        <ProductForm
          product={editing === 'new' ? null : editing}
          prices={editing === 'new' ? null : priceMap.get(editing.id)}
          lists={lists}
          onSave={saveProduct}
          showTax={showTax}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
